/** Slash suggestion menu primitives used by @tiptap/suggestion. */
import { APP_Z_INDEX } from "@pathwright/ux/src/components/style/zIndex"
import type { Editor } from "@tiptap/core"
import type { CSSProperties } from "react"
import * as React from "react"
import { createRoot, type Root } from "react-dom/client"
import type { IconType } from "react-icons"
export interface CommandItem {
  id: string
  label: string
  description: string
  icon: IconType
}

export interface FloatingMenuProps<T = unknown> {
  items: T[]
  selectedIndex: number
  onSelect: (item: T) => void
  clientRect?: (() => DOMRect | null) | null | undefined
}

const getSelectionClientRect = (editor: Editor): DOMRect | null => {
  try {
    const pos = editor.state.selection.$anchor.pos
    const coords = editor.view.coordsAtPos(pos)
    return new DOMRect(
      coords.left,
      coords.top,
      coords.right - coords.left,
      coords.bottom - coords.top
    )
  } catch {
    return null
  }
}

const MENU_WIDTH = 320
const MENU_MAX_HEIGHT = 260
const MENU_VIEWPORT_PADDING = 8
const MENU_OFFSET = 8
const MENU_Z_INDEX = 2147483647
//
function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getMenuPosition(rect: DOMRect | null) {
  if (!rect) {
    return {
      left: MENU_VIEWPORT_PADDING,
      top: MENU_VIEWPORT_PADDING
    }
  }

  const maxLeft = Math.max(
    MENU_VIEWPORT_PADDING,
    window.innerWidth - MENU_WIDTH - MENU_VIEWPORT_PADDING
  )

  const left = clamp(rect.left, MENU_VIEWPORT_PADDING, maxLeft)

  const belowTop = rect.bottom + MENU_OFFSET
  const aboveTop = rect.top - MENU_MAX_HEIGHT - MENU_OFFSET
  const top =
    belowTop + MENU_MAX_HEIGHT <= window.innerHeight - MENU_VIEWPORT_PADDING
      ? belowTop
      : Math.max(MENU_VIEWPORT_PADDING, aboveTop)

  return { left, top }
}

function useMenuPosition(
  clientRect: (() => DOMRect | null) | null | undefined
) {
  const clientRectRef = React.useRef<typeof clientRect>(clientRect)
  const [position, setPosition] = React.useState<{
    left: number
    top: number
  } | null>(null)

  React.useEffect(() => {
    clientRectRef.current = clientRect
  }, [clientRect])

  React.useLayoutEffect(() => {
    let frame = 0
    let cancelled = false

    const updatePosition = () => {
      if (cancelled) return

      const rect = clientRectRef.current?.()
      if (rect) {
        const next = getMenuPosition(rect)

        setPosition((prev) =>
          prev && prev.left === next.left && prev.top === next.top ? prev : next
        )
      }
      // console.log(
      //   "[slash] rect",
      //   rect,
      //   "position",
      //   rect ? getMenuPosition(rect) : null
      // )

      frame = window.requestAnimationFrame(updatePosition)
    }

    updatePosition()

    return () => {
      cancelled = true
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  return {
    style: position
      ? {
          position: "fixed",
          left: `${position.left}px`,
          top: `${position.top}px`
        }
      : {
          position: "fixed",
          visibility: "hidden",
          left: 0,
          top: 0
        }
  }
}

export function createSuggestionRender<T extends { id: string }>(
  MenuComponent: React.ComponentType<FloatingMenuProps<T>>
) {
  return () => {
    let container: HTMLDivElement | null = null
    let root: Root | null = null
    let selectedIndex = 0
    let latestProps: {
      items: T[]
      editor: Editor
      clientRect?: (() => DOMRect | null) | null
      command: (item: T) => void
    } | null = null

    const getClientRect = () =>
      latestProps?.clientRect?.() ??
      (latestProps ? getSelectionClientRect(latestProps.editor) : null)

    const render = () => {
      if (!root || !latestProps) return
      root.render(
        <MenuComponent
          items={latestProps.items}
          selectedIndex={selectedIndex}
          onSelect={(item: T) => latestProps?.command(item)}
          clientRect={getClientRect}
        />
      )
    }

    return {
      onStart(props: {
        items: T[]
        editor: Editor
        clientRect?: (() => DOMRect | null) | null
        command: (item: T) => void
      }) {
        selectedIndex = 0
        latestProps = props
        container = document.createElement("div")
        document.body.appendChild(container)
        root = createRoot(container)
        render()
      },

      onUpdate(props: {
        items: T[]
        editor: Editor
        clientRect?: (() => DOMRect | null) | null
        command: (item: T) => void
      }) {
        if (!root) return
        latestProps = props
        render()
      },

      onKeyDown({ event }: { event: KeyboardEvent }) {
        if (!latestProps || !root) return false

        const itemCount = latestProps.items.length
        if (itemCount === 0) return false

        if (event.key === "ArrowUp") {
          selectedIndex = (selectedIndex - 1 + itemCount) % itemCount
          render()
          return true
        }

        if (event.key === "ArrowDown") {
          selectedIndex = (selectedIndex + 1) % itemCount
          render()
          return true
        }

        if (event.key === "Enter" || event.key === "NumpadEnter") {
          const item = latestProps.items[selectedIndex]
          if (item) latestProps.command(item)
          return true
        }

        if (event.key === "Escape") return false

        return false
      },

      onExit() {
        root?.unmount()
        root = null
        container?.remove()
        container = null
        latestProps = null
      }
    }
  }
}

export const SuggestionMenu = React.forwardRef<
  HTMLDivElement,
  FloatingMenuProps<CommandItem>
>(function SuggestionMenu(props, ref) {
  const { items, selectedIndex, onSelect, clientRect } = props
  const { style } = useMenuPosition(clientRect)

  const selectedRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" })
  }, [selectedIndex])

  return (
    <div
      ref={ref}
      style={
        {
          ...(style ?? {}),
          zIndex: APP_Z_INDEX + 1,
          background: "#fff",
          color: "#111827",
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
          overflow: "hidden",
          minWidth: "240px",
          maxWidth: `${MENU_WIDTH}px`,
          maxHeight: `${MENU_MAX_HEIGHT}px`,
          overflowY: "auto",
          pointerEvents: "auto",
          fontFamily: "inherit"
        } as CSSProperties
      }
      role="listbox"
      aria-label="Suggestions"
    >
      {items.length === 0 ? (
        <div
          style={{
            padding: "12px",
            textAlign: "center",
            fontSize: "13px",
            color: "#6b7280"
          }}
        >
          No suggestions found
        </div>
      ) : (
        items.map((item, index) => {
          const Icon = item.icon
          return (
            <div
              key={item.id}
              ref={index === selectedIndex ? selectedRef : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px",
                cursor: "pointer",
                background:
                  index === selectedIndex
                    ? "rgba(17,24,39,0.06)"
                    : "transparent"
              }}
              onPointerDown={(event) => {
                event.preventDefault()
                onSelect(item)
              }}
              data-highlighted={index === selectedIndex ? "" : undefined}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  minWidth: "28px",
                  borderRadius: "9999px",
                  background: "rgba(17,24,39,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Icon size={16} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  {item.description}
                </div>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
})
